import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { eachWeekOfInterval, endOfWeek, max, min, startOfWeek } from 'date-fns';
import { addWeeks } from 'date-fns/fp';

import { useNow } from '@/hooks/useNow';
import type { Task } from '@/project-management/entities/task';
import type { Workstream } from '@/project-management/entities/workstream';

import { TimelineGridelines } from './timeline-gridelines';
import { TimelineTodayLine } from './timeline-todayline';
import { TimelineWorkstream } from './timeline-workstream';

export type TimelineProps = {
  workstreams: (Pick<Workstream, 'id' | 'startDate' | 'endDate' | 'name'> & {
    tasks: Pick<Task, 'id' | 'title' | 'dueDate' | 'status' | 'priority'>[];
  })[];
};

const ROW_WIDTH_PX = 50;
const TIMELINE_MINIMUM_RANGE = addWeeks(17);

export function Timeline({ workstreams }: TimelineProps) {
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const now = useNow();
  const dates = useMemo(() => {
    if (!workstreams.length) {
      return undefined;
    }

    const dates = workstreams.flatMap((workstream) => [
      workstream.startDate,
      workstream.endDate,
      ...workstream.tasks.flatMap((task) =>
        task.dueDate ? [task.dueDate] : [],
      ),
    ]);

    const start = min(dates);
    const end = max([...dates, TIMELINE_MINIMUM_RANGE(start)]);
    const firstWeekStart = startOfWeek(start);
    const lastWeekEnd = endOfWeek(end);
    const weeks = eachWeekOfInterval({
      start: firstWeekStart,
      end: lastWeekEnd,
    });

    return { firstWeekStart, lastWeekEnd, start, end, weeks };
  }, [workstreams]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    scrollElement.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftGradient(scrollLeft > 0);
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  if (!dates) {
    return null;
  }

  return (
    <>
      <h3 className="text-lg font-semibold">Project Timeline</h3>

      <div className="relative w-full">
        <div
          ref={scrollRef}
          className="grid h-full min-h-[300px] grid-cols-[theme(width.32)_1fr] overflow-y-auto"
        >
          <div className="sticky left-0 z-10 h-full bg-white">
            <div className="mb-4 h-8 w-full border-b border-gray-200"></div>
            <div className="w-full divide-y divide-gray-100">
              {workstreams.map((ws) => (
                <div
                  key={ws.id}
                  className="flex h-16 items-center justify-start pr-2"
                >
                  <Link
                    to={{
                      pathname: './workstreams',
                      search: new URLSearchParams({ id: ws.id }).toString(),
                    }}
                    className="line-clamp-2 text-sm font-medium text-gray-700 hover:underline"
                  >
                    {ws.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>
          <div
            className="relative overflow-hidden"
            style={{ width: ROW_WIDTH_PX * dates.weeks.length }}
          >
            <TimelineGridelines
              start={dates.firstWeekStart}
              end={dates.lastWeekEnd}
            />

            <div className="mb-4 h-8 w-full border-b border-gray-200"></div>

            <div className="w-full divide-y divide-gray-100">
              {workstreams.map((workstream) => (
                <div
                  key={workstream.id}
                  className="flex h-16 items-center justify-stretch"
                >
                  <TimelineWorkstream
                    workstream={workstream}
                    start={dates.firstWeekStart}
                    end={dates.lastWeekEnd}
                  />
                </div>
              ))}
            </div>

            <TimelineTodayLine
              now={now}
              start={dates.firstWeekStart}
              end={dates.lastWeekEnd}
            />
          </div>
        </div>
        {showLeftGradient && (
          <div className="pointer-events-none absolute inset-y-0 bottom-3 left-32 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
        )}
        {showRightGradient && (
          <div className="pointer-events-none absolute inset-y-0 bottom-3 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />
        )}
      </div>
    </>
  );
}
